/**
 * Pure drop→move translation for the assembly drag-and-drop (feature 015).
 * Converts a dnd-kit drag-end (`active` node over an `over` target) into the
 * contract's afterId vocabulary (`null` ⇒ move to the top), enforcing the
 * client-side constraints: sections reorder among sections, subsections stay
 * within their OWN section, and test cases move within or BETWEEN subsections
 * (including append-drops onto a subsection row or a table's blank space).
 * Returns `null` for no-ops and invalid targets — the server re-validates
 * everything regardless.
 */

export type AssemblyShape = {
  readonly sections: ReadonlyArray<{
    readonly id: string;
    readonly subsections: ReadonlyArray<{
      readonly id: string;
      readonly testCaseIds: readonly string[];
    }>;
  }>;
};

export type DragKind = 'section' | 'subsection' | 'testCase';

/** Data carried by every sortable node (the drag side). */
export type DragNodeData = { readonly kind: DragKind; readonly label: string };

/** Data carried by append drop targets (a subsection row / a table's frame). */
export type DropTargetData = {
  readonly kind: 'subsectionDrop' | 'caseListDrop';
  readonly subsectionId: string;
};

export type AssemblyMove =
  | {
      readonly kind: 'section';
      readonly sectionId: string;
      readonly afterSectionId: string | null;
    }
  | {
      readonly kind: 'subsection';
      readonly subsectionId: string;
      readonly afterSubsectionId: string | null;
    }
  | {
      readonly kind: 'testCase';
      readonly testCaseId: string;
      readonly toSubsectionId: string;
      readonly afterTestCaseId: string | null;
    };

/**
 * The afterId for moving `fromId` onto `toId`'s slot within one ordered list —
 * the classic sortable mapping: moving DOWN lands after the hovered item;
 * moving UP lands before it. `undefined` ⇒ invalid or a no-op.
 */
const afterIdWithinList = (
  ids: readonly string[],
  fromId: string,
  toId: string,
): string | null | undefined => {
  const from = ids.indexOf(fromId);
  const to = ids.indexOf(toId);
  if (from === -1 || to === -1 || from === to) return undefined;
  if (to > from) return ids[to] ?? undefined;
  return to === 0 ? null : (ids[to - 1] ?? undefined);
};

const subsectionParent = (shape: AssemblyShape, subsectionId: string) =>
  shape.sections.find((section) =>
    section.subsections.some((subsection) => subsection.id === subsectionId),
  );

const caseParent = (shape: AssemblyShape, testCaseId: string) =>
  shape.sections
    .flatMap((section) => section.subsections)
    .find((subsection) => subsection.testCaseIds.includes(testCaseId));

const subsectionById = (shape: AssemblyShape, subsectionId: string) =>
  shape.sections
    .flatMap((section) => section.subsections)
    .find((subsection) => subsection.id === subsectionId);

export const translateDragEnd = (
  shape: AssemblyShape,
  activeId: string,
  activeKind: DragKind,
  overId: string,
  overData: DragNodeData | DropTargetData | undefined,
): AssemblyMove | null => {
  if (activeId === overId) return null;

  if (activeKind === 'section') {
    const afterSectionId = afterIdWithinList(
      shape.sections.map((section) => section.id),
      activeId,
      overId,
    );
    if (afterSectionId === undefined) return null;
    return { kind: 'section', sectionId: activeId, afterSectionId };
  }

  if (activeKind === 'subsection') {
    // The drop may resolve to a subsection's append target — a `subsectionDrop`
    // droppable that wraps the row and shadows the subsection sortable under
    // closestCorners. Treat it as a drop onto THAT subsection so the reorder
    // still lands instead of being rejected as an unknown target (F-130). Only
    // `subsectionDrop` is resolved here; `caseListDrop` stays a test-case concern.
    const overSubsectionId = overData?.kind === 'subsectionDrop' ? overData.subsectionId : overId;
    const parent = subsectionParent(shape, activeId);
    const overParent = subsectionParent(shape, overSubsectionId);
    // Same-section reorders only (the server enforces this too).
    if (parent === undefined || overParent === undefined || parent.id !== overParent.id) {
      return null;
    }
    const afterSubsectionId = afterIdWithinList(
      parent.subsections.map((subsection) => subsection.id),
      activeId,
      overSubsectionId,
    );
    if (afterSubsectionId === undefined) return null;
    return { kind: 'subsection', subsectionId: activeId, afterSubsectionId };
  }

  // activeKind === 'testCase'
  const source = caseParent(shape, activeId);
  if (source === undefined) return null;

  // Append drops: a subsection row (even collapsed) or a table's blank space.
  if (overData?.kind === 'subsectionDrop' || overData?.kind === 'caseListDrop') {
    const destination = subsectionById(shape, overData.subsectionId);
    if (destination === undefined) return null;
    const others = destination.testCaseIds.filter((id) => id !== activeId);
    const afterTestCaseId = others.at(-1) ?? null;
    // Already last in this list ⇒ nothing to do.
    if (source.id === destination.id && destination.testCaseIds.at(-1) === activeId) return null;
    return {
      kind: 'testCase',
      testCaseId: activeId,
      toSubsectionId: destination.id,
      afterTestCaseId,
    };
  }

  // Row-relative drop: over another test case, in the same or another subsection.
  const destination = caseParent(shape, overId);
  if (destination === undefined) return null;
  if (source.id === destination.id) {
    const afterTestCaseId = afterIdWithinList(destination.testCaseIds, activeId, overId);
    if (afterTestCaseId === undefined) return null;
    return {
      kind: 'testCase',
      testCaseId: activeId,
      toSubsectionId: destination.id,
      afterTestCaseId,
    };
  }
  // Cross-subsection: land BEFORE the hovered row (it is displaced down).
  const overIndex = destination.testCaseIds.indexOf(overId);
  if (overIndex === -1) return null;
  const afterTestCaseId = overIndex === 0 ? null : (destination.testCaseIds[overIndex - 1] ?? null);
  return {
    kind: 'testCase',
    testCaseId: activeId,
    toSubsectionId: destination.id,
    afterTestCaseId,
  };
};
